import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  MapPin, 
  Star, 
  Clock, 
  Users,
  BookOpen,
  GraduationCap,
  X,
  SlidersHorizontal
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { tuitionTeacherService } from '../firebase/tuition';
import TeacherProfile from './TeacherProfile';

const TeacherSearch = () => {
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [filters, setFilters] = useState({
    subjects: [],
    classes: [],
    locality: '',
    maxFees: '',
    availability: [],
    minRating: 0,
    experience: ''
  });

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
    'Hindi', 'Social Studies', 'History', 'Geography', 'Economics',
    'Computer Science', 'Accountancy', 'Business Studies', 'Psychology',
    'Political Science', 'Sociology', 'Philosophy', 'Literature'
  ];

  const classes = [
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
    'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
    'Class 11', 'Class 12', 'JEE Main', 'JEE Advanced', 'NEET',
    'UPSC', 'SSC', 'Banking', 'Railway', 'Defense'
  ];

  const availabilityOptions = [
    { value: 'online', label: 'Online Classes' },
    { value: 'home_visit', label: 'Home Visit' },
    { value: 'student_visit', label: 'Student Visits' }
  ];

  useEffect(() => {
    loadTeachers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [teachers, filters, searchQuery]);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const teachersData = await tuitionTeacherService.searchTeachers({ status: 'approved' });
      setTeachers(teachersData);
    } catch (error) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...teachers];

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(teacher =>
        teacher.name.toLowerCase().includes(query) ||
        teacher.subjects.some(subject => subject.toLowerCase().includes(query)) ||
        teacher.classes.some(cls => cls.toLowerCase().includes(query)) ||
        teacher.locality.toLowerCase().includes(query) ||
        teacher.city.toLowerCase().includes(query)
      );
    }

    // Subject filter
    if (filters.subjects.length > 0) {
      filtered = filtered.filter(teacher =>
        filters.subjects.some(subject => teacher.subjects.includes(subject))
      );
    }

    // Class filter
    if (filters.classes.length > 0) {
      filtered = filtered.filter(teacher =>
        filters.classes.some(cls => teacher.classes.includes(cls))
      );
    }

    // Locality filter
    if (filters.locality) {
      filtered = filtered.filter(teacher =>
        teacher.locality.toLowerCase().includes(filters.locality.toLowerCase()) ||
        teacher.city.toLowerCase().includes(filters.locality.toLowerCase())
      );
    }

    // Max fees filter
    if (filters.maxFees) {
      filtered = filtered.filter(teacher =>
        teacher.feesPerHour <= parseInt(filters.maxFees)
      );
    }

    // Availability filter
    if (filters.availability.length > 0) {
      filtered = filtered.filter(teacher =>
        filters.availability.some(avail => teacher.availability.includes(avail))
      );
    }

    // Rating filter
    if (filters.minRating > 0) {
      filtered = filtered.filter(teacher =>
        teacher.rating >= filters.minRating
      );
    }

    // Experience filter
    if (filters.experience) {
      const exp = parseInt(filters.experience);
      filtered = filtered.filter(teacher =>
        teacher.experience >= exp
      );
    }

    setFilteredTeachers(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleArrayFilterChange = (filterType, value, checked) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: checked
        ? [...prev[filterType], value]
        : prev[filterType].filter(item => item !== value)
    }));
  };

  const clearFilters = () => {
    setFilters({
      subjects: [],
      classes: [],
      locality: '',
      maxFees: '',
      availability: [],
      minRating: 0,
      experience: ''
    });
    setSearchQuery('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.subjects.length > 0) count++;
    if (filters.classes.length > 0) count++;
    if (filters.locality) count++;
    if (filters.maxFees) count++;
    if (filters.availability.length > 0) count++;
    if (filters.minRating > 0) count++;
    if (filters.experience) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teachers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Your Perfect Tutor</h1>
          <p className="text-gray-600">Discover qualified teachers for personalized learning</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name, subject, class, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SlidersHorizontal className="mr-2" size={20} />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-1">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Subjects Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subjects
                  </label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {subjects.map((subject) => (
                      <label key={subject} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={filters.subjects.includes(subject)}
                          onChange={(e) => handleArrayFilterChange('subjects', subject, e.target.checked)}
                          className="mr-2"
                        />
                        {subject}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Classes Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Classes
                  </label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {classes.map((cls) => (
                      <label key={cls} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={filters.classes.includes(cls)}
                          onChange={(e) => handleArrayFilterChange('classes', cls, e.target.checked)}
                          className="mr-2"
                        />
                        {cls}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city or locality"
                    value={filters.locality}
                    onChange={(e) => handleFilterChange('locality', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Max Fees Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Fees (₹/hour)
                  </label>
                  <input
                    type="number"
                    placeholder="Enter max fees"
                    value={filters.maxFees}
                    onChange={(e) => handleFilterChange('maxFees', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Availability Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability
                  </label>
                  <div className="space-y-1">
                    {availabilityOptions.map((option) => (
                      <label key={option.value} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={filters.availability.includes(option.value)}
                          onChange={(e) => handleArrayFilterChange('availability', option.value, e.target.checked)}
                          className="mr-2"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Rating
                  </label>
                  <select
                    value={filters.minRating}
                    onChange={(e) => handleFilterChange('minRating', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Any Rating</option>
                    <option value={1}>1+ Stars</option>
                    <option value={2}>2+ Stars</option>
                    <option value={3}>3+ Stars</option>
                    <option value={4}>4+ Stars</option>
                    <option value={5}>5 Stars</option>
                  </select>
                </div>

                {/* Experience Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Experience (Years)
                  </label>
                  <input
                    type="number"
                    placeholder="Enter min experience"
                    value={filters.experience}
                    onChange={(e) => handleFilterChange('experience', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Clear Filters Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Found {filteredTeachers.length} teacher{filteredTeachers.length !== 1 ? 's' : ''}
            {getActiveFiltersCount() > 0 && ' matching your criteria'}
          </p>
        </div>

        {/* Teachers Grid */}
        {filteredTeachers.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No teachers found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search criteria or filters
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeachers.map((teacher) => (
              <TeacherCard
                key={teacher.id}
                teacher={teacher}
                onClick={() => setSelectedTeacher(teacher)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Teacher Profile Modal */}
      {selectedTeacher && (
        <TeacherProfile
          teacherId={selectedTeacher.id}
          onClose={() => setSelectedTeacher(null)}
        />
      )}
    </div>
  );
};

// Teacher Card Component
const TeacherCard = ({ teacher, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start space-x-4 mb-4">
          {teacher.photoUrl ? (
            <img
              src={teacher.photoUrl}
              alt={teacher.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
              <GraduationCap size={24} className="text-gray-400" />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {teacher.name}
            </h3>
            <div className="flex items-center mb-1">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className={i < Math.floor(teacher.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="ml-1 text-sm text-gray-600">({teacher.rating || 0})</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <MapPin size={14} className="mr-1" />
              {[teacher.locality, teacher.city, teacher.state].filter(Boolean).join(', ')}
            </div>
          </div>
        </div>

        {/* Subjects */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {teacher.subjects?.slice(0, 3).map((subject) => (
              <span
                key={subject}
                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {subject}
              </span>
            ))}
            {teacher.subjects?.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                +{teacher.subjects.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Classes */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {teacher.classes?.slice(0, 3).map((cls) => (
              <span
                key={cls}
                className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
              >
                {cls}
              </span>
            ))}
            {teacher.classes?.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                +{teacher.classes.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Availability */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {teacher.availability?.map((option) => (
              <span
                key={option}
                className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
              >
                {option === 'online' ? 'Online' : 
                 option === 'home_visit' ? 'Home Visit' : 
                 'Student Visits'}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center text-sm text-gray-600">
            <span className="mr-1 text-sm font-semibold">₹</span>
            {teacher.feesPerHour}/hour
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Clock size={14} className="mr-1" />
            {teacher.experience} years
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherSearch;
